import React, { useEffect, useState } from "react";
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import useGetAuthenticatedDevices from "../../../react-query-hooks/AuthenticatedDevices/useGetAuthenticatedDevices";
import useRemoveAllDevices from "../../../react-query-hooks/AuthenticatedDevices/useRemoveAllDevices";
import useRemoveDevice from "../../../react-query-hooks/AuthenticatedDevices/useRemoveDevice";
import { useAppDispatch } from "../../../redux/store/store";
import { unauthorizeLogout } from "../../../context/UserAuthentication/actions";
import { Toast_Func } from "../../../helpers/toast.helper";
import Spinner from "../../Spinner/Spinner";
import AuthenticatedDeviceCard from "./authenticatedDeviceCard";
import NoAuthenticatedDevices from "./noAuthenticatedDevices";
import { CustomModal } from "../../Modal/CustomModal";
import ConfirmationModal from "../../ConfirmatioModal/Confirmation";
import DefaultMethodConfirmationModal from "../DefaultMethodConfirmation/DefaultMethodConfirmation";
import { useQueryClient } from "react-query";
import { CUSTOMER_MICRO_SERVICE, ROUTE_PROFILE_DEVICES } from "../../../constants";
import OtpVerificationModal from "../../../containers/account/OtpVeficationModal";
import axiosWithInterceptor from "../../../services/axiosWithInterceptor";

const AuthenticatedDevices: React.FC = () => {
    const history = useHistory();
    const { data: allDevices, isLoading } = useGetAuthenticatedDevices();
    const { mutateAsync: deleteDevices, isLoading: removingDevices } = useRemoveAllDevices();
    const { mutateAsync: deleteDevice, isLoading: removingDevice } = useRemoveDevice();
    const dispatch = useAppDispatch();
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [confirmationModalText, setConfirmationModalText] = useState<string>("");
    const [deviceToken, setDeviceToken] = useState<string>(null);
    const queryClient = useQueryClient();
    const [displayOtpModal, setDisplayOtpModal] = useState<boolean>(false);
    const [otpExpiryTime, setOtpExpiryTime] = useState<string>(null);
    const [isSendingOtp, setIsSendingOtp] = useState<boolean>(false);
    const [email, setEmail] = useState<string>(null);
    let listedDeviceLimit = 3;
    const hideConfirmationModal = () => {
        setShowConfirmationModal(false);
        setDeviceToken(null);
    }
    const removeAllDevices = async (values, setSubmitting) => {
        setSubmitting(true);
        deleteDevices(
            { otp_code: values.otp },
        )
            .then(data => {
                hideConfirmationModal();
                unauthorizeLogout(dispatch);
                history.push('/');
                Toast_Func({
                    status: true,
                    message: "Devices removed successfully.",
                });
            })
            .catch(error => {
                Toast_Func({
                    status: false,
                    message: error.response.data.message,
                });
            })
            .finally(() => {
                setSubmitting(false);
            })
    }

    const removeSingleDevice = () => {
        deleteDevice(deviceToken)
            .then(data => {
                let deletedDevice = allDevices.find((device) => device.token === deviceToken);
                if (deletedDevice.current_device) {
                    unauthorizeLogout(dispatch);
                    history.push('/');
                }
                else { queryClient.invalidateQueries('authDevices'); }
                hideConfirmationModal();
                Toast_Func({
                    status: true,
                    message: "Device removed successfully.",
                });
            })
            .catch(error => {
                Toast_Func({
                    status: false,
                    message: error.response.data.message,
                });
            })
    }

    const handleSendOtp = async () => {
        setIsSendingOtp(true);
        try {
            const response = await axiosWithInterceptor.post
                (`${CUSTOMER_MICRO_SERVICE}/send-otp`, { "is_authenticated_user": true });
            setOtpExpiryTime(response.data.data.expires_at);
            setEmail(response.data.data.email);
            setShowConfirmationModal(false);
            setDisplayOtpModal(true);
            return true;
        } catch (error) {
            if (Boolean(error?.response?.data?.otp_popup)) {
                setOtpExpiryTime(error?.response?.data?.data?.expires_at);
                setEmail(error?.response?.data?.data?.email);
                setShowConfirmationModal(false);
                setDisplayOtpModal(Boolean(error?.response?.data?.otp_popup));
            }
            Toast_Func({
                status: false,
                message: error?.response?.data?.message || error?.response?.data?.errors?.[0],
            });
            return false;
        }
        finally {
            setIsSendingOtp(false);
        }
    }
    const handleResendOtp = async (setSubmitting) => {
        setSubmitting(true);
        const isOtpSent = await handleSendOtp();
        setSubmitting(false);
    }

    const handleRemoveAllDevice = () => {
        setConfirmationModalText("You’re requesting to Logout from all Devices. Would you like to continue?");
        setShowConfirmationModal(true);
    }

    const handleRemoveDevice = (deviceToken: string) => {
        setConfirmationModalText("You’re requesting to logout of a Device. Would you like to continue?");
        setShowConfirmationModal(true);
        setDeviceToken(deviceToken);
    }

    const handleModalConfirmation = (setSubmitting = null) => {
        if (deviceToken && !setSubmitting) { removeSingleDevice(); }
        else { handleSendOtp(); }
    }
    const handleBack = () => {
        history.goBack();
    };

    return (
        <>
            <div className="text-start cz-mb-32 d-block d-md-none">
                <div
                    onClick={handleBack}
                    className="btn btn-cutom back-arrow-btn f-s14 px-0 mb-0 text-capitalize position-absolue ps-0"
                >
                    back
                </div>
            </div>
            {
                allDevices?.length > 0 ?
                    <>
                        <h4 className="font-Cls f-sm-s22 f-s24 text-start h-24 medium_text mb-4 ">
                            All Authenticated Devices
                        </h4>
                        <>
                            {
                                allDevices.slice(0, listedDeviceLimit).map((device) => (
                                    <AuthenticatedDeviceCard
                                        device={device}
                                        handleRemoveDevice={handleRemoveDevice}
                                    />
                                ))
                            }
                        </>

                        {allDevices?.length > listedDeviceLimit && (
                            <Link
                                className="btn btn-custom text_underline font-Visby-cf f-w6 f-s14 clr-dark-blue p-0 ls-normal"
                                to={'/profileDetails/more-devices'}
                            >
                                View More...
                            </Link>
                        )}
                        <button
                            onClick={() => handleRemoveAllDevice()}
                            type="button"
                            className="btn btn-large d-block m-auto mt-5"
                        >
                            Logout from all Devices
                        </button>
                        <hr className="custom_hr_sty cz-my-40 d-none d-md-block"></hr>
                    </>
                    :
                    <NoAuthenticatedDevices />
            }
            <DefaultMethodConfirmationModal
                show={showConfirmationModal}
                title="Confirmation"
                content={confirmationModalText}
                btnText={"Logout"}
                handleClick={(e) => {
                    handleModalConfirmation();
                }}
                handleClose={() => {
                    setShowConfirmationModal(false)
                }}
                shouldDisableButton = {removingDevice || isSendingOtp}
            />
            <OtpVerificationModal
                displayOtpModal={displayOtpModal}
                setDisplayOtpModal={setDisplayOtpModal}
                handleOtpVerification={removeAllDevices}
                resendOtp={handleResendOtp}
                userEmail={email}
                otpExpiryTime={otpExpiryTime}
            />
        </>
    );
};
export default AuthenticatedDevices;
